var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import '../../styles/widgets/popup/coloredTilesManipulativePopup.scss';
import { useCallback, useContext, useState, useRef, useEffect } from 'react';
import { t } from 'i18next';
import { v4 as uuidv4 } from 'uuid';
import { DataContext } from '../../player/DataContext';
import FooterActionsButtons from './FooterActionsButtons';
import ColoredTiles, { TILE_COLORS } from '../ColoredTiles';
import RadioButton from '../../components/RadioButton';
import ToggleButton from '../../components/ToggleButton';
import ButtonComponent from '../../components/ButtonComponent';
import globalStore from '../../store';
import CustomScrollbar from '../../components/CustomScrollbar';
import { saveState } from '../../helper/stateManagerHelper';
import customEventHandler from '../../helper/customEvents';
import { snapToBackgroundGrid } from '../../helper/snapHelper';
var generateId = function () { return "coloredTiles_".concat(uuidv4().replace(/-/g, '')); };
var generateRandomPartColors = function (rows, cols) {
    var colors = {};
    for (var r = 0; r < rows; r++) {
        for (var c = 0; c < cols; c++) {
            colors["".concat(r, "-").concat(c)] = TILE_COLORS[Math.floor(Math.random() * TILE_COLORS.length)];
        }
    }
    return colors;
};
var DEFAULT_DATA = {
    id: generateId(),
    type: 'coloredTiles',
    mode: 'grid',
    preview: false,
    rows: 5,
    columns: 5,
    cellValue: 1,
    scale: 1,
    center: { x: 0, y: 0 },
    dimensions: { width: 550, height: 550 },
    lock: { positioning: false, editing: false },
    generationMode: 'random',
    dimensionLabels: false,
    topLinearSegments: [],
    leftLinearSegments: [],
    isGroup: false,
    partColors: generateRandomPartColors(5, 5),
};
var MAX_ROW_COL = 12;
var ColoredTilesManipulativePopup = function () {
    var _a;
    var _b = useContext(DataContext), setManipulative = _b.setManipulative, setSelectedManipulativeId = _b.setSelectedManipulativeId, setManiplulatorComponentVisible = _b.setManiplulatorComponentVisible, setActiveManipulator = _b.setActiveManipulator, getPositionForNewItem = _b.getPositionForNewItem, setSelectedTool = _b.setSelectedTool;
    var _c = useState(DEFAULT_DATA), coloredTilesData = _c[0], setColoredTilesData = _c[1];
    // Screen-reader announcement for popup state changes (grid dimensions after a
    // row/column change, and the Red linear pieces / Dimension labels toggles).
    // Rendered into an in-popup aria-live region (a body-level announcer is not
    // reliably read while the popup is open).
    var _d = useState(''), gridSrMessage = _d[0], setGridSrMessage = _d[1];
    var btnConfig = globalStore.useStoreState(function (state) { return state.jsonDataStore; }).btnConfig;
    useEffect(function () {
        var handleWheel = function (e) {
            if (e.ctrlKey) {
                e.preventDefault();
            }
        };
        var handleKeyDown = function (e) {
            if (e.ctrlKey &&
                (e.key === '=' || e.key === '-' || e.key === '+' || e.key === '0')) {
                e.preventDefault();
            }
        };
        var handleTouchStart = function (e) {
            if (e.touches.length > 1) {
                e.preventDefault();
            }
        };
        document.addEventListener('wheel', handleWheel, { passive: false });
        document.addEventListener('keydown', handleKeyDown);
        document.addEventListener('touchstart', handleTouchStart, { passive: false });
        return function () {
            document.removeEventListener('wheel', handleWheel);
            document.removeEventListener('keydown', handleKeyDown);
            document.removeEventListener('touchstart', handleTouchStart);
        };
    }, []);
    var previewContainerRef = useRef(null);
    var randomControlsRef = useRef(null);
    var _e = useState(0), mainAreaMinHeight = _e[0], setMainAreaMinHeight = _e[1];
    // Wrapper-driven floor for the mainArea height: ensures the wrapper renders at
    // at least MIN_SCALE (0.45) so it never looks tiny. Reads preview's vertical
    // padding live (it's defined in SCSS as a viewport-relative clamp) so the
    // floor adapts to every screen size.
    var computeWrapperRequiredHeight = useCallback(function () {
        var PREVIEW_SIZE = 418;
        var MIN_SCALE = 0.45;
        var BUFFER = 8;
        var RESERVED_LABEL_TOP = 66; // always reserved so toggling doesn't shift layout
        var cellSize = Math.floor(Math.min(PREVIEW_SIZE / coloredTilesData.columns, PREVIEW_SIZE / coloredTilesData.rows));
        var naturalH = coloredTilesData.rows * cellSize;
        var paddingY = 32; // fallback
        var previewEl = previewContainerRef.current;
        if (previewEl) {
            var cs = window.getComputedStyle(previewEl);
            paddingY = (parseFloat(cs.paddingTop) || 0) + (parseFloat(cs.paddingBottom) || 0);
        }
        return MIN_SCALE * (naturalH + RESERVED_LABEL_TOP) + 2 * BUFFER + paddingY;
    }, [coloredTilesData.rows, coloredTilesData.columns]);
    useEffect(function () {
        var el = randomControlsRef.current;
        var recompute = function () {
            var _a;
            var togglesH = (_a = el === null || el === void 0 ? void 0 : el.offsetHeight) !== null && _a !== void 0 ? _a : 0;
            setMainAreaMinHeight(Math.max(togglesH, computeWrapperRequiredHeight()));
        };
        recompute();
        if (!el)
            return;
        var observer = new ResizeObserver(recompute);
        observer.observe(el);
        window.addEventListener('resize', recompute);
        return function () {
            observer.disconnect();
            window.removeEventListener('resize', recompute);
        };
    }, [coloredTilesData.generationMode, computeWrapperRequiredHeight]);
    // Scale the coloredTilesWrapper inside the preview container to fit, including
    // dimension labels (X above, Y to left). All sizes are computed from data — we
    // never read the wrapper's bounding rect, so concurrent calls / ResizeObserver
    // firings cannot race on a transient transform value.
    var updatePreviewScale = useCallback(function () {
        var container = previewContainerRef.current;
        if (!container)
            return;
        var wrapper = container.querySelector('.coloredTilesWrapper');
        if (!wrapper)
            return;
        var containerRect = container.getBoundingClientRect();
        var cs = window.getComputedStyle(container);
        var pTop = parseFloat(cs.paddingTop) || 0;
        var pBottom = parseFloat(cs.paddingBottom) || 0;
        var pLeft = parseFloat(cs.paddingLeft) || 0;
        var pRight = parseFloat(cs.paddingRight) || 0;
        var availW = containerRect.width - pLeft - pRight;
        var availH = containerRect.height - pTop - pBottom;
        // Natural wrapper size — derived from data, matches ColoredTiles.tsx cellSize
        // formula at preview mode (see ColoredTiles.tsx around line 750).
        var PREVIEW_SIZE = 418;
        var rows = coloredTilesData.rows, columns = coloredTilesData.columns;
        var cellSize = Math.floor(Math.min(PREVIEW_SIZE / columns, PREVIEW_SIZE / rows));
        var naturalW = columns * cellSize;
        var naturalH = rows * cellSize;
        // Label extras — ALWAYS reserved (regardless of dimensionLabels toggle) so the
        // wrapper's size and position don't shift when the user toggles labels on/off.
        // Numbers based on font-size: 2.1875rem (≈35 px), line-height ~1.2 (≈42 px),
        // margin: 1.5rem X / 2rem Y in coloredTiles.scss .coloredTilesDimLabelsX/Y.
        var LABEL_HEIGHT_PX = 42;
        var LABEL_X_MARGIN_PX = 24; // 1.5rem
        var LABEL_Y_MARGIN_PX = 32; // 2rem
        var LABEL_TEXT_WIDTH_PX = 36; // widest needed (2-digit "12") — generous
        var extraTop = LABEL_HEIGHT_PX + LABEL_X_MARGIN_PX;
        var extraLeft = LABEL_TEXT_WIDTH_PX + LABEL_Y_MARGIN_PX;
        var effectiveW = naturalW + extraLeft;
        var effectiveH = naturalH + extraTop;
        if (!effectiveW || !effectiveH)
            return;
        // Small inset so labels don't sit flush against the popup chrome.
        var BUFFER = 8;
        var MIN_GAP_FROM_TOGGLES = 24;
        // Toggle-aware horizontal constraint: when the absolute-positioned randomControls
        // (toggles) is present, the wrapper is right-shifted to clear it. The wrapper+label
        // horizontal extent then becomes
        //   (naturalW + extraLeft) * scale + togglesRightFromContent + MIN_GAP_FROM_TOGGLES
        // which must still fit inside `availW - BUFFER`. Without this constraint the right
        // shift would push the preview off the popup's right edge for wide previews.
        var togglesEl = randomControlsRef.current;
        var togglesRightFromContent = togglesEl ? togglesEl.offsetLeft + togglesEl.offsetWidth - pLeft : 0;
        var scaleHFitWithToggles = togglesRightFromContent > 0
            ? (availW - BUFFER - togglesRightFromContent - MIN_GAP_FROM_TOGGLES) /
                (naturalW + extraLeft)
            : Infinity;
        var scale = Math.max(0, Math.min((availW - 2 * BUFFER) / effectiveW, (availH - 2 * BUFFER) / effectiveH, scaleHFitWithToggles, 1));
        // Translate the wrapper so the label+wrapper UNION is centered (rather than
        // just the wrapper, which would push the label too close to the top/left).
        // Add an extra right shift ONLY when the Y-label would otherwise sit too
        // close to the absolute-positioned randomControls (toggles) on the left.
        var baseDx = (extraLeft * scale) / 2;
        // Y label visible left, in coordinates relative to container's left padding edge.
        // Wrapper center sits at availW/2 (flex-centered) + baseDx; label is extraLeft*scale further left.
        var yLabelLeftFromContent = availW / 2 + baseDx - (naturalW * scale) / 2 - extraLeft * scale;
        var overlapNeeded = togglesRightFromContent + MIN_GAP_FROM_TOGGLES - yLabelLeftFromContent;
        var extraShift = Math.max(0, overlapNeeded);
        var dx = baseDx + extraShift;
        var dy = (extraTop * scale) / 2;
        // setProperty('important') beats `.coloredTilesWrapper.isPreview` in
        // coloredTiles.scss which applies `transform: scale(...) !important` per
        // media query. Inline-style-with-important wins over stylesheet-with-important.
        wrapper.style.setProperty('transform', "translate(".concat(dx, "px, ").concat(dy, "px) scale(").concat(scale, ")"), 'important');
        wrapper.style.setProperty('transform-origin', 'center center', 'important');
    }, [coloredTilesData.rows, coloredTilesData.columns]);
    useEffect(function () {
        updatePreviewScale();
    }, [updatePreviewScale, coloredTilesData.rows, coloredTilesData.columns, coloredTilesData.generationMode]);
    useEffect(function () {
        var container = previewContainerRef.current;
        if (!container)
            return;
        var observer = new ResizeObserver(function () { return updatePreviewScale(); });
        observer.observe(container);
        return function () { return observer.disconnect(); };
    }, [updatePreviewScale]);
    // -------------------------------
    // Helpers
    // -------------------------------
    var updateRows = function (delta) {
        // Announce the full new dimensions (total rows × columns) via an in-popup
        // live region — the bare-number count display alone only reads "6" to a
        // screen reader, not the labeled totals.
        var announcedRows = Math.min(MAX_ROW_COL, Math.max(1, coloredTilesData.rows + delta));
        if (announcedRows !== coloredTilesData.rows) {
            setGridSrMessage(t('text.coloredTilesGridResized', {
                rows: announcedRows,
                columns: coloredTilesData.columns,
            }));
        }
        setColoredTilesData(function (prev) {
            var newRows = Math.min(MAX_ROW_COL, Math.max(1, prev.rows + delta));
            if (newRows === prev.rows)
                return prev;
            var newLeftSegments = prev.leftLinearSegments || [];
            var wasFull = newLeftSegments.length === prev.rows;
            newLeftSegments = newLeftSegments.filter(function (idx) { return idx < newRows; });
            if (wasFull && newRows > prev.rows) {
                for (var i = prev.rows; i < newRows; i++) {
                    if (!newLeftSegments.includes(i))
                        newLeftSegments.push(i);
                }
            }
            var newPartColors = prev.partColors || {};
            if (prev.generationMode === 'random') {
                if (newRows > prev.rows) {
                    newPartColors = __assign({}, newPartColors);
                    for (var r = prev.rows; r < newRows; r++) {
                        for (var c = 0; c < prev.columns; c++) {
                            newPartColors["".concat(r, "-").concat(c)] = TILE_COLORS[Math.floor(Math.random() * TILE_COLORS.length)];
                        }
                    }
                }
                else {
                    newPartColors = Object.fromEntries(Object.entries(newPartColors).filter(function (_a) {
                        var key = _a[0];
                        return parseInt(key.split('-')[0]) < newRows;
                    }));
                }
            }
            return __assign(__assign({}, prev), { rows: newRows, leftLinearSegments: newLeftSegments, partColors: newPartColors });
        });
    };
    var updateCols = function (delta) {
        // Announce the full new dimensions (total rows × columns).
        var announcedCols = Math.min(MAX_ROW_COL, Math.max(1, coloredTilesData.columns + delta));
        if (announcedCols !== coloredTilesData.columns) {
            setGridSrMessage(t('text.coloredTilesGridResized', {
                rows: coloredTilesData.rows,
                columns: announcedCols,
            }));
        }
        setColoredTilesData(function (prev) {
            var newCols = Math.min(MAX_ROW_COL, Math.max(1, prev.columns + delta));
            if (newCols === prev.columns)
                return prev;
            var newTopSegments = prev.topLinearSegments || [];
            var wasFull = newTopSegments.length === prev.columns;
            newTopSegments = newTopSegments.filter(function (idx) { return idx < newCols; });
            if (wasFull && newCols > prev.columns) {
                for (var i = prev.columns; i < newCols; i++) {
                    if (!newTopSegments.includes(i))
                        newTopSegments.push(i);
                }
            }
            var newPartColors = prev.partColors || {};
            if (prev.generationMode === 'random') {
                if (newCols > prev.columns) {
                    newPartColors = __assign({}, newPartColors);
                    for (var r = 0; r < prev.rows; r++) {
                        for (var c = prev.columns; c < newCols; c++) {
                            newPartColors["".concat(r, "-").concat(c)] = TILE_COLORS[Math.floor(Math.random() * TILE_COLORS.length)];
                        }
                    }
                }
                else {
                    newPartColors = Object.fromEntries(Object.entries(newPartColors).filter(function (_a) {
                        var key = _a[0];
                        return parseInt(key.split('-')[1]) < newCols;
                    }));
                }
            }
            return __assign(__assign({}, prev), { columns: newCols, topLinearSegments: newTopSegments, partColors: newPartColors });
        });
    };
    // -------------------------------
    // Actions
    // -------------------------------
    var onAction = function (type) {
        var _a;
        if (type === 'done') {
            if (coloredTilesData.generationMode === 'manual') {
                var cellSize = 110;
                var _b = getPositionForNewItem(), x = _b.x, y = _b.y;
                customEventHandler.dispatchEvent('bottomPanel', {
                    id: 'createColoredTile',
                    data: {
                        color: TILE_COLORS[0],
                        x: x - cellSize / 2,
                        y: y - cellSize / 2,
                        isGroup: false,
                        isInvisible: true,
                    },
                });
            }
            else {
                var cellSize_1 = 110;
                var rows = coloredTilesData.rows;
                var columns = coloredTilesData.columns;
                var _c = getPositionForNewItem(), x = _c.x, y = _c.y;
                var snappedPos = snapToBackgroundGrid(x, y, 110);
                var newColoredTilesId_1 = generateId();
                var newManipulative_1 = {
                    type: 'coloredTiles',
                    widgetId: "coloredTiles:".concat(uuidv4()),
                    data: __assign(__assign({}, coloredTilesData), { id: newColoredTilesId_1, rows: rows, columns: columns, dimensions: {
                            width: columns * cellSize_1,
                            height: rows * cellSize_1,
                        }, center: { x: snappedPos.x, y: snappedPos.y }, preview: false, cutRows: [], cutColumns: [], openCuts: { rows: [], cols: [] }, showExpression: false, showProduct: false, partColors: coloredTilesData.partColors || {}, partModeEnabled: false, selectModeEnabled: false, isGroup: coloredTilesData.generationMode === "random", leftLinearSegments: [], topLinearSegments: [] }),
                };
                var tileLeft_1 = snappedPos.x - (columns * cellSize_1) / 2;
                var tileTop_1 = snappedPos.y - (rows * cellSize_1) / 2;
                // Build snappedVerticalPieceIds: one slot per row, null if no piece for that row
                var snappedVIds_1 = Array(rows).fill(null);
                var verticalPieces = (coloredTilesData.leftLinearSegments || []).map(function (rowIdx) {
                    var newId = "linearPiece_".concat(uuidv4().replace(/-/g, ''));
                    snappedVIds_1[rowIdx] = newId;
                    return {
                        type: 'linearPiece',
                        widgetId: "linearPiece:".concat(uuidv4()),
                        data: {
                            id: newId, type: 'linearPiece', orientation: 'vertical',
                            center: { x: tileLeft_1 - 22, y: tileTop_1 + rowIdx * cellSize_1 + cellSize_1 / 2 },
                            scale: 1, dimensions: { width: 10, height: 102 },
                            lock: { positioning: false, editing: false },
                        },
                    };
                });
                // Build snappedHorizontalPieceIds: one slot per column, null if no piece for that column
                var snappedHIds_1 = Array(columns).fill(null);
                var horizontalPieces = (coloredTilesData.topLinearSegments || []).map(function (colIdx) {
                    var newId = "linearPiece_".concat(uuidv4().replace(/-/g, ''));
                    snappedHIds_1[colIdx] = newId;
                    return {
                        type: 'linearPiece',
                        widgetId: "linearPiece:".concat(uuidv4()),
                        data: {
                            id: newId, type: 'linearPiece', orientation: 'horizontal',
                            center: { x: tileLeft_1 + colIdx * cellSize_1 + cellSize_1 / 2, y: tileTop_1 - 21 },
                            scale: 1, dimensions: { width: 102, height: 10 },
                            lock: { positioning: false, editing: false },
                        },
                    };
                });
                // Persist the piece IDs so addRow/addColumn know to create matching pieces
                if (snappedVIds_1.some(function (id) { return id !== null; })) {
                    newManipulative_1.data.snappedVerticalPieceIds = snappedVIds_1;
                }
                if (snappedHIds_1.some(function (id) { return id !== null; })) {
                    newManipulative_1.data.snappedHorizontalPieceIds = snappedHIds_1;
                }
                var allNewPieces_1 = __spreadArray(__spreadArray([], verticalPieces, true), horizontalPieces, true);
                // Group the tile with its snapped linear pieces by default so they
                // drag together (same fields the "Group" action / duplicate-with-pieces
                // path assign — see docs/manipulatives/ColoredTiles.md "External LinearPiece").
                if (allNewPieces_1.length > 0) {
                    var newCtGroupId_1 = "manipulativeGroup_".concat(uuidv4().replace(/-/g, ''));
                    Object.assign(newManipulative_1.data, {
                        groupId: newCtGroupId_1,
                        groupPosition: 0,
                        groupCenter: undefined,
                        beforeGroupScale: (_a = newManipulative_1.data.scale) !== null && _a !== void 0 ? _a : 1,
                        groupScale: 1,
                        isGroup: true,
                    });
                    allNewPieces_1.forEach(function (piece, idx) {
                        var _a;
                        Object.assign(piece.data, {
                            groupId: newCtGroupId_1,
                            groupPosition: idx + 1,
                            groupCenter: undefined,
                            beforeGroupScale: (_a = piece.data.scale) !== null && _a !== void 0 ? _a : 1,
                            groupScale: 1,
                        });
                    });
                }
                setManipulative(function (prev) { return __spreadArray(__spreadArray(__spreadArray([], prev, true), [newManipulative_1], false), allNewPieces_1, true); });
                var stateEntries = __spreadArray([
                    { type: newColoredTilesId_1, id: newColoredTilesId_1.split('_')[1], propertyName: 'create', old: JSON.stringify(newManipulative_1) }
                ], allNewPieces_1.map(function (p) { return ({
                    type: p.data.id,
                    id: p.data.id.split('_')[1],
                    propertyName: 'create',
                    old: JSON.stringify(p),
                }); }), true);
                saveState(stateEntries);
                setTimeout(function () {
                    setSelectedTool([newColoredTilesId_1]);
                }, 0);
                setTimeout(function () {
                    customEventHandler.dispatchEvent('toggleRightSidePanel', { isOpen: true });
                }, 100);
            }
        }
        setSelectedManipulativeId('');
        setManiplulatorComponentVisible(false);
        setActiveManipulator('');
    };
    // -------------------------------
    // UI
    // -------------------------------
    return (_jsx("div", { className: "coloredTilesPopup", children: _jsxs("div", { className: "holder", children: [_jsx(CustomScrollbar, { direction: "vertical", children: _jsxs("div", { className: "scrollbarContent", children: [_jsxs("div", { className: "coloredTilesHeader", children: [_jsx("h2", { id: "ct-popup-title", className: "title", children: t('text.coloredTiles') }), _jsx("fieldset", { className: "coloredTilesTypeSelector", "aria-label": t('text.coloredTilesTypeLabel'), children: _jsx(RadioButton, { id: "colored-tiles-mode-selector", options: [t('text.coloredTilesRandom'), t('text.coloredTilesOutline'), t('text.coloredTilesManual')], selected: coloredTilesData.generationMode === 'random' ? 0 : coloredTilesData.generationMode === 'outline' ? 1 : 2, onChange: function (index) {
                                                var modes = ['random', 'outline', 'manual'];
                                                var nextMode = modes[index];
                                                setColoredTilesData(__assign(__assign({}, DEFAULT_DATA), { id: coloredTilesData.id, generationMode: nextMode, 
                                                    // mode: (nextMode === 'random' || nextMode === 'manual') ? 'grid' : 'open',
                                                    rows: nextMode === 'manual' ? 1 : DEFAULT_DATA.rows, columns: nextMode === 'manual' ? 1 : DEFAULT_DATA.columns, partColors: nextMode === 'manual' ? { '0-0': '#FFFFFF' } : nextMode === 'random' ? generateRandomPartColors(DEFAULT_DATA.rows, DEFAULT_DATA.columns) : {} }));
                                            }, className: "coloredTilesRadioInline" }) })] }), _jsxs("div", { className: "coloredTilesMainArea", style: { minHeight: mainAreaMinHeight || undefined }, children: [coloredTilesData.generationMode !== "manual" && (_jsxs("div", { className: "randomControls", ref: randomControlsRef, children: [_jsxs("div", { className: "toggleBox", role: "group", "aria-labelledby": "ct-toggle-linear-label", children: [_jsx("span", { id: "ct-toggle-linear-label", className: "toggleLabel", children: t('text.redLinearPieces') }), _jsx(ToggleButton, { className: "onOffToggle", initial: ((_a = coloredTilesData.topLinearSegments) === null || _a === void 0 ? void 0 : _a.length) || 0 > 0 ? 'on' : 'off', options: [
                                                            { value: 'on', valueVisible: 'text.on', ariaLabel: 'text.on' },
                                                            { value: 'off', valueVisible: 'text.off', ariaLabel: 'text.off' },
                                                        ], onChange: function (val) {
                                                            setGridSrMessage(t(val === 'on'
                                                                ? 'text.coloredTilesLinearPiecesOn'
                                                                : 'text.coloredTilesLinearPiecesOff'));
                                                            setColoredTilesData(function (prev) { return (__assign(__assign({}, prev), { redLinearPieces: val === 'on', leftLinearSegments: val === 'on' ? Array(prev.rows).fill(0).map(function (_, idx) { return idx; }) : [], topLinearSegments: val === 'on' ? Array(prev.columns).fill(0).map(function (_, idx) { return idx; }) : [] })); });
                                                        } }, "redLinearPieces_".concat(coloredTilesData.generationMode))] }), _jsxs("div", { className: "toggleBox", role: "group", "aria-labelledby": "ct-toggle-labels-label", children: [_jsx("span", { id: "ct-toggle-labels-label", className: "toggleLabel", children: t('text.dimensionLabels') }), _jsx(ToggleButton, { className: "onOffToggle", initial: coloredTilesData.dimensionLabels ? 'on' : 'off', options: [
                                                            { value: 'on', valueVisible: 'text.on', ariaLabel: 'text.on' },
                                                            { value: 'off', valueVisible: 'text.off', ariaLabel: 'text.off' },
                                                        ], onChange: function (val) {
                                                            setGridSrMessage(t(val === 'on'
                                                                ? 'text.coloredTilesDimensionLabelsOn'
                                                                : 'text.coloredTilesDimensionLabelsOff'));
                                                            setColoredTilesData(function (prev) { return (__assign(__assign({}, prev), { dimensionLabels: val === 'on' })); });
                                                        } }, "dimensionLabels_".concat(coloredTilesData.generationMode))] })] })), _jsx("div", { className: "coloredTilesPreview", ref: previewContainerRef, "aria-hidden": "true", children: _jsx(ColoredTiles, { data: __assign(__assign({}, coloredTilesData), { center: { x: 0, y: 0 }, preview: true }) }) })] }), _jsxs("div", { className: "operationContainer", children: [_jsx("div", { "aria-live": "polite", "aria-atomic": "true", role: "status", style: {
                                            position: 'absolute',
                                            width: '1px',
                                            height: '1px',
                                            padding: 0,
                                            margin: '-1px',
                                            overflow: 'hidden',
                                            clip: 'rect(0, 0, 0, 0)',
                                            whiteSpace: 'nowrap',
                                            border: 0,
                                        }, children: gridSrMessage }), _jsxs("div", { className: "headerWrapper", "aria-hidden": coloredTilesData.generationMode === 'manual' ? true : undefined, children: [_jsxs("div", { className: "leftContainer", role: "group", "aria-label": t('text.rows'), children: [_jsxs("span", { className: coloredTilesData.generationMode === 'manual' ? 'displayCount_disable' : '', children: [_jsx("b", { children: t('text.rows') }), "\u00A0(1-12)"] }), _jsx("div", { className: "dividerLine" }), _jsx(ButtonComponent, { imgSrc: btnConfig.minus.imgSrc, defaultCSS: "incDecBtn", normalCSS: "incDecBtn", clickHandler: function () { return updateRows(-1); }, disabled: coloredTilesData.rows <= 1 || coloredTilesData.generationMode === 'manual', clickActive: false, ariaLabel: t('text.arrayModelDecreaseRows') }), _jsx("div", { className: "displayCount ".concat(coloredTilesData.generationMode === 'manual' ? 'displayCount_disable' : ''), children: coloredTilesData.rows }), _jsx(ButtonComponent, { imgSrc: btnConfig.plus.imgSrc, defaultCSS: "incDecBtn", normalCSS: "incDecBtn", clickHandler: function () { return updateRows(1); }, disabled: coloredTilesData.rows >= 12 ||
                                                            coloredTilesData.generationMode === 'manual', clickActive: false, ariaLabel: t('text.arrayModelIncreaseRows') }), _jsx("div", { className: "dividerLine" })] }), _jsxs("div", { className: "rightContainer", role: "group", "aria-label": t('text.columns'), children: [_jsxs("span", { className: coloredTilesData.generationMode === 'manual' ? 'displayCount_disable' : '', children: [_jsx("b", { children: t('text.columns') }), "\u00A0(1-12)"] }), _jsx("div", { className: "dividerLine" }), _jsx(ButtonComponent, { imgSrc: btnConfig.minus.imgSrc, defaultCSS: "incDecBtn", normalCSS: "incDecBtn", clickHandler: function () { return updateCols(-1); }, disabled: coloredTilesData.columns <= 1 || coloredTilesData.generationMode === 'manual', clickActive: false, ariaLabel: t('text.arrayModelDecreaseColumns') }), _jsx("div", { className: "displayCount ".concat(coloredTilesData.generationMode === 'manual' ? 'displayCount_disable' : ''), children: coloredTilesData.columns }), _jsx(ButtonComponent, { imgSrc: btnConfig.plus.imgSrc, defaultCSS: "incDecBtn", normalCSS: "incDecBtn", clickHandler: function () { return updateCols(1); }, disabled: coloredTilesData.columns >= 12 ||
                                                            coloredTilesData.generationMode === 'manual', clickActive: false, ariaLabel: t('text.arrayModelIncreaseColumns') }), _jsx("div", { className: "dividerLine" })] })] })] })] }) }), _jsx(FooterActionsButtons, { onActionBtnClick: onAction })] }) }));
};
export default ColoredTilesManipulativePopup;
